/**
 * Janelas horárias independentes por dia.
 *
 * Módulo puro, sem IO. Cada dia tem a sua própria janela (início/fim em
 * minutos locais), com `localDate` + `timeZone` internos para que os horários
 * de abertura sejam avaliados no dia da semana real (segunda ≠ domingo).
 *
 * O `weekday` é SEMPRE derivado de `localDate + timeZone` — nunca recebido
 * como valor independente. Nada disto entra no trace: só as origens
 * (`startSource`/`endSource`).
 */

export type DayWindowSource =
  | "default"
  | "user_start"
  | "arrival_logistics"
  | "departure_logistics"
  | "day_end_default";

export type DayWindow = {
  day: number;
  /** Data local "YYYY-MM-DD" (interna, nunca no trace). */
  localDate?: string;
  /** Fuso IANA do destino (interno, nunca no trace). */
  timeZone?: string;
  startMinutes: number;
  endMinutes: number;
  startSource: DayWindowSource;
  endSource: DayWindowSource;
};

/** Fim de dia por defeito: um pedido normal nunca passa da meia-noite. */
export const DEFAULT_DAY_END_MINUTES = 22 * 60;
/** Minutos úteis mínimos para considerar um dia viável. */
export const MIN_VIABLE_DAY_MINUTES = 90;

const DAY_MS = 86_400_000;

const pad = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" a partir de uma data, opcionalmente no fuso do destino. */
export function localDateString(date: Date, timeZone?: string): string {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date);
      const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
      const y = get("year");
      const m = get("month");
      const d = get("day");
      if (y && m && d) return `${y}-${m}-${d}`;
    } catch {
      /* fuso inválido: cai no relógio local */
    }
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Dia da semana (0 = domingo) derivado de `localDate` (+ fuso quando falta). */
export function weekdayFor(window: Pick<DayWindow, "localDate" | "timeZone">): number {
  const iso = window.localDate;
  if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-").map((n) => Number.parseInt(n, 10));
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }
  return new Date(localDateString(new Date(), window.timeZone) + "T00:00:00Z").getUTCDay();
}

export type BuildDayWindowsInput = {
  totalDays: number;
  /** Hora normal de início (minutos do dia). */
  defaultStartMinutes: number;
  /** Fim de dia por defeito (minutos do dia). */
  defaultEndMinutes?: number;
  /** Primeiro dia do roteiro; sem data usa hoje. */
  startDate?: Date | string | null;
  timeZone?: string;
  /** Início real do dia 1 (chegada declarada + overhead). */
  day1StartMinutes?: number;
  /** Fim do último dia imposto pela partida (bloco de aeroporto). */
  lastDayEndMinutes?: number;
};

function toDate(value: Date | string | null | undefined): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split("-").map((n) => Number.parseInt(n, 10));
    return new Date(y, m - 1, d);
  }
  return new Date();
}

/**
 * Uma janela por dia. A chegada só afeta o dia 1 e a partida só afeta o
 * último dia; os dias intermédios usam sempre a janela normal.
 */
export function buildDayWindows(input: BuildDayWindowsInput): DayWindow[] {
  const total = Math.max(1, Math.round(input.totalDays || 1));
  const defaultEnd = input.defaultEndMinutes ?? DEFAULT_DAY_END_MINUTES;
  const first = toDate(input.startDate);
  const windows: DayWindow[] = [];

  for (let day = 1; day <= total; day++) {
    const date = new Date(first.getTime() + (day - 1) * DAY_MS);
    let startMinutes = input.defaultStartMinutes;
    let startSource: DayWindowSource = "user_start";
    let endMinutes = defaultEnd;
    let endSource: DayWindowSource = "day_end_default";

    if (day === 1 && input.day1StartMinutes != null) {
      startMinutes = Math.max(input.day1StartMinutes, 0);
      startSource = "arrival_logistics";
    }
    if (day === total && input.lastDayEndMinutes != null) {
      endMinutes = Math.min(endMinutes, Math.max(0, input.lastDayEndMinutes));
      endSource = "departure_logistics";
    }

    windows.push({
      day,
      localDate: localDateString(date, input.timeZone),
      timeZone: input.timeZone,
      startMinutes,
      endMinutes,
      startSource,
      endSource,
    });
  }
  return windows;
}

/** Minutos úteis da janela (nunca negativos). */
export const windowMinutes = (w: DayWindow) => Math.max(0, w.endMinutes - w.startMinutes);

/** A janela do dia é viável para pelo menos uma visita? */
export const isWindowViable = (w: DayWindow, minMinutes = MIN_VIABLE_DAY_MINUTES) =>
  windowMinutes(w) >= minMinutes;

export function findWindow(windows: DayWindow[], day: number): DayWindow | undefined {
  return windows.find((w) => w.day === day);
}
